import { useParams, Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Phone, ArrowRight, Check, ChevronDown, ArrowLeft } from "lucide-react";
import NavBar from "@/components/site/NavBar";
import Footer from "@/components/site/Footer";
import StickyCallButton from "@/components/site/StickyCallButton";
import { services } from "@/data/services";
import useSEO from "@/hooks/useSEO";
import { buildServiceJsonLd, buildServiceFaqJsonLd, SITE_URL } from "@/data/structuredData";

const ServicePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const s = services[slug];
  const [openFaq, setOpenFaq] = useState(0);

  useSEO({
    title: s?.metaTitle,
    description: s?.metaDescription,
    canonical: s ? `${SITE_URL}/services/${s.slug}` : undefined,
    jsonLd: s ? [buildServiceJsonLd(s), buildServiceFaqJsonLd(s)] : undefined,
  });

  if (!s) {
    return (
      <div className="min-h-screen bg-[#F4F4F5]">
        <NavBar />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <h1 className="font-display uppercase text-4xl mb-4">Service not found</h1>
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 bg-[#D9480F] text-white px-5 py-3 font-display uppercase tracking-tight border-2 border-black"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      <NavBar />

      {/* Breadcrumbs */}
      <div className="border-b border-zinc-900 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-600">
          <Link to="/" className="hover:text-[#D9480F]">Home</Link>
          <span>/</span>
          <Link to="/#services" className="hover:text-[#D9480F]">Services</Link>
          <span>/</span>
          <span className="text-[#D9480F] font-bold">{s.title}</span>
        </div>
      </div>

      {/* Hero */}
      <section className="border-b border-zinc-900 bg-[#F4F4F5]">
        <div className="bg-grid relative">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D9480F] font-bold mb-4">
                {s.code} · Pittsburgh service
              </p>
              <h1 className="font-display uppercase font-semibold leading-[0.9] tracking-tighter text-5xl md:text-7xl mb-6">
                {s.h1}
              </h1>
              <p className="font-display text-xl md:text-2xl text-[#D9480F] mb-6">
                {s.tagline}
              </p>
              <p className="text-zinc-700 text-base md:text-lg leading-relaxed max-w-2xl">
                {s.hero}
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <a
                  href="tel:4847643392"
                  className="inline-flex items-center gap-3 bg-[#D9480F] text-white px-6 py-4 font-display uppercase tracking-tight text-base hover:bg-[#E8590C] border-2 border-black hard-shadow-sm"
                >
                  <Phone className="w-5 h-5" strokeWidth={2.5} />
                  Call 484-764-3392
                </a>
                <Link
                  to="/#contact"
                  className="inline-flex items-center gap-3 bg-transparent border-2 border-[#0B2B40] text-[#0B2B40] px-6 py-4 font-display uppercase tracking-tight text-base hover:bg-[#0B2B40] hover:text-white"
                >
                  Free Inspection →
                </Link>
              </div>
            </div>
            <aside className="lg:col-span-4">
              <div className="bg-[#0B2B40] text-white border-2 border-black p-6 hard-shadow-orange">
                <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#F59E0B] font-bold mb-2">
                  Pests We Handle
                </div>
                <ul className="space-y-2 text-sm">
                  {s.pests.map((p) => (
                    <li key={p} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#F59E0B] mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Signs */}
      <section className="border-b border-zinc-900 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D9480F] font-bold mb-4">
            Warning Signs
          </p>
          <h2 className="font-display uppercase text-3xl md:text-5xl font-semibold tracking-tighter leading-[0.9] mb-10">
            How to tell if <br />
            <span className="text-[#0B2B40]">you have a problem.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {s.signs.map((sign, i) => (
              <div
                key={i}
                className="bg-[#F4F4F5] border-2 border-zinc-900 p-5 flex items-start gap-4"
              >
                <div className="w-9 h-9 bg-[#D9480F] text-white flex items-center justify-center font-display text-lg flex-shrink-0">
                  {i + 1}
                </div>
                <p className="text-sm md:text-base leading-relaxed text-zinc-800">
                  {sign}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="border-b border-zinc-900 bg-[#0B2B40] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#F59E0B] font-bold mb-4">
            Our Process
          </p>
          <h2 className="font-display uppercase text-3xl md:text-5xl font-semibold tracking-tighter leading-[0.9] mb-12">
            No guessing. <br />
            <span className="text-[#F59E0B]">Just results.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {s.process.map((step, i) => (
              <article
                key={i}
                className="bg-[#143b58] border-2 border-white p-6"
              >
                <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#F59E0B] font-bold mb-3">
                  Step 0{i + 1}
                </div>
                <h3 className="font-display uppercase text-xl tracking-tight mb-3">
                  {step.title}
                </h3>
                <p className="text-zinc-200 text-sm leading-relaxed">
                  {step.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b border-zinc-900 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D9480F] font-bold mb-4">
            Common Questions
          </p>
          <h2 className="font-display uppercase text-3xl md:text-5xl font-semibold tracking-tighter leading-[0.9] mb-10">
            {s.title.split(" ")[0]} FAQs.
          </h2>
          <div className="border-t-2 border-black">
            {s.faqs.map((f, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={i} className="border-b-2 border-black">
                  <button
                    onClick={() => setOpenFaq(isOpen ? -1 : i)}
                    className="w-full flex items-start justify-between gap-6 py-5 text-left group"
                  >
                    <span className="font-display uppercase text-base md:text-lg tracking-tight leading-tight group-hover:text-[#D9480F]">
                      {f.q}
                    </span>
                    <ChevronDown
                      className={`w-5 h-5 flex-shrink-0 transition-transform ${
                        isOpen ? "rotate-180 text-[#D9480F]" : ""
                      }`}
                      strokeWidth={2.5}
                    />
                  </button>
                  {isOpen && (
                    <div className="pb-5 pr-8 text-zinc-700 leading-relaxed border-l-2 border-[#D9480F] pl-4">
                      {f.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#D9480F]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-20 text-center">
          <h2 className="font-display uppercase text-4xl md:text-6xl text-white font-semibold tracking-tighter leading-[0.9] mb-6">
            Got the problem? <br />
            We got a guy.
          </h2>
          <p className="text-white/90 text-base md:text-lg mb-8 max-w-2xl mx-auto">
            Free inspection. Written quote. No pressure. Serving Pittsburgh and Allegheny County since 2020.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href="tel:4847643392"
              className="inline-flex items-center gap-3 bg-black text-white px-6 py-4 font-display uppercase tracking-tight border-2 border-black"
            >
              <Phone className="w-5 h-5" strokeWidth={2.5} />
              Call 484-764-3392
            </a>
            <Link
              to="/#contact"
              className="inline-flex items-center gap-3 bg-white text-black px-6 py-4 font-display uppercase tracking-tight border-2 border-black"
            >
              Book Online
              <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <StickyCallButton />
    </div>
  );
};

export default ServicePage;
